import React, { useEffect, useState } from "react";
import { Activity, Crown, FlaskConical, ScrollText } from "lucide-react";
import api from "@/lib/api";
import { translateFr as tfr } from "@/i18n";
import { Badge } from "@/components/ui/badge";

const fmtAt = (v) => {
  try { return new Date(v).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return String(v || "").slice(0, 16); }
};

export const FounderOverview = () => {
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/admin/founder/overview").then((r) => setD(r.data)).catch(() => setD(false));
  }, []);

  if (!d) return null;
  const a = d.account || {};
  const events = [
    ...(d.audit?.admin_events || []).map((e) => ({ label: e.event, at: e.at })),
    ...(d.audit?.security_events || []).map((e) => ({ label: e.label || e.type, at: e.created_at || e.at })),
  ].filter((e) => e.at).sort((x, y) => String(y.at).localeCompare(String(x.at))).slice(0, 8);

  return (
    <section className="rounded-2xl border border-border p-4 sm:p-5 space-y-4" data-testid="founder-overview">
      <div className="flex items-center gap-2">
        <Crown className="h-4.5 w-4.5 text-athlynx" />
        <h2 className="text-base font-semibold">{tfr("Statut Fondateur")}</h2>
        {a.billing_exempt && (
          <Badge variant="secondary" className="text-[10px]" data-testid="founder-exempt-badge">
            {tfr("Accès permanent")}
          </Badge>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
        <div className="rounded-lg bg-secondary/40 p-3 space-y-1.5" data-testid="founder-health-card">
          <p className="flex items-center gap-1.5 font-medium"><Activity className="h-3.5 w-3.5" /> {tfr("Santé de la plateforme")}</p>
          <p className="text-muted-foreground">
            {tfr("Base de données")} : <span className={d.health?.db_ok ? "text-emerald-500" : "text-red-500"}>
              {d.health?.db_ok ? tfr("Opérationnelle") : tfr("Erreur")}</span>
          </p>
          <p className="text-muted-foreground">{tfr("Heure serveur")} : {fmtAt(d.health?.server_time)}</p>
        </div>
        <div className="rounded-lg bg-secondary/40 p-3 space-y-1.5" data-testid="founder-entitlement-card">
          <p className="flex items-center gap-1.5 font-medium"><Crown className="h-3.5 w-3.5" /> {tfr("Abonnement")}</p>
          <p className="text-muted-foreground">
            {a.billing_exempt ? tfr("Exonération interne — aucune expiration") : a.subscription_status}
          </p>
          <p className="text-muted-foreground">{(d.entitlements || []).length} {tfr("fonctionnalités débloquées")}</p>
          <p className="text-muted-foreground">2FA : {a.two_factor_enabled ? tfr("activée") : tfr("disponible (non activée)")}</p>
        </div>
        <div className="rounded-lg bg-secondary/40 p-3 space-y-1.5" data-testid="founder-demo-card">
          <p className="flex items-center gap-1.5 font-medium"><FlaskConical className="h-3.5 w-3.5" /> {tfr("Mode démo")}</p>
          <p className="text-muted-foreground">
            {d.demo?.demo_mode ? tfr("Actif — sans limite de durée") : tfr("Inactif — activable à tout moment")}
          </p>
          <p className="text-muted-foreground">{d.demo?.demo_players || 0} {tfr("joueurs démo")}</p>
        </div>
      </div>
      {events.length > 0 && (
        <div className="space-y-1.5" data-testid="founder-audit-list">
          <p className="flex items-center gap-1.5 text-xs font-medium"><ScrollText className="h-3.5 w-3.5" /> {tfr("Derniers événements d'audit")}</p>
          <ul className="text-[11px] text-muted-foreground space-y-0.5">
            {events.map((e, i) => (
              <li key={i} className="flex justify-between gap-3">
                <span className="truncate">{e.label}</span>
                <span className="shrink-0">{fmtAt(e.at)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};
